import React, { useMemo } from 'react';

import { blanketInsuranceApis } from '@apis/next/blanketInsuranceApis';
import { fileUploadApis } from '@apis/next/fileUploadApis';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import { FormTitle } from '@components/Forms/FormTitle';
import { Layout } from '@components/Layout';
import { FileArrayInput } from '@components/NextForms/FileArrayInput';
import {
  ManagementFormContainer,
  ManagementFormLabel,
  ManagementFormInput,
} from '@components/NextForms/ManagementForm';
import { QueryContainer } from '@components/QueryContainer';
import { Container } from '@components/utilities/Contact/Container';
import { VerticalSpace } from '@components/VerticalSpace';
import { instructionStrings } from '@data/instruction';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { useFormUtils } from '@hooks/useFormUtils';
import {
  BlanketInsurance,
  BlanketInsuranceActionStatus,
  BlanketInsuranceForm,
  BlanketInsuranceHelpers,
} from '@models/next/BlanketInsurance';
import { FileTypeHelper } from '@models/next/FileType';
import { empty } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { useQueryClient } from '@tanstack/react-query';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { formatDate } from '@utils/types';
import { stringify } from 'query-string';
import { Link, useHistory } from 'react-router-dom';
import { z } from 'zod';

const strings = createStrings({
  ja: {
    title: instructionStrings.insuranceTags.BLANKET,
    createBlanketInsurance: `${instructionStrings.insuranceTags.BLANKET}の新規登録`,
    blanketInsuranceInfo: `${instructionStrings.insuranceTags.BLANKET}情報`,
    placeholders: {
      commodity_no: '例：152364789',
      name_en: '例：CHAIR',
      name_ja: '例：椅子',
      explanation: '編み方、形状など',
      memo: '参考HS CODEがあれば、教えてください。商品カテゴリーが分かれば、ここに記入してください。例えば、雑貨、機械、アパレルなど。',
      hs_code: 'D2D担当者記入欄',
    },
    localBlanketInsuranceFilesHint: '＊保険証券の写真やコピーなどをアップロードしてください。',
    create: '登録',
    createSuccess: '正常に登録されました。',
    editSuccess: '正常に更新されました。',
    deleteHint: `${instructionStrings.insuranceTags.BLANKET}情報を削除しますか？復元するには再入力して頂きます。`,
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  body: {
    ...common.flexColumn,
    flex: 1,
  },
  buttonContainer: {
    ...common.flexCenter,
    ...common.colSpanFull,
    columnGap: '20px',
  },
  dateRangeContainer: {
    ...common.middleText,
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr auto',
    columnGap: '0.5rem',
    '& > span': {
      paddingTop: 5,
    },
  },
  hint: {
    marginTop: 5,
    ...common.textGray700,
    ...common.smallerText,
  },
  blanketInsuranceDisplay: {
    ...common.middleText,
    backgroundColor: theme.palette.grey[50],
    padding: 20,
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    columnGap: 80,
    rowGap: 22,
  },
}));

const BlanketInsuranceCreateEditForm: React.FC<
  BlanketInsuranceCreateDetailsProps & {
    blanketInsurance?: BlanketInsurance;
  }
> = ({ type, id, blanketInsurance }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const history = useHistory();

  const form = useForm(BlanketInsuranceForm, {
    initialOutput: useMemo(
      () => (blanketInsurance ? BlanketInsuranceHelpers.mapToForm(blanketInsurance) : empty),
      [blanketInsurance],
    ),
    enableReinitialize: true,
  });

  const { fields } = form.root;

  const queryClient = useQueryClient();

  const refresh = () =>
    queryClient.refetchQueries(blanketInsuranceApis.getKeyByPath('get', '/blanket-insurance/:id'));

  const postBlanketInsurance = blanketInsuranceApis.usePost('/blanket-insurance');

  const patchBlanketInsurance = blanketInsuranceApis.usePatch('/blanket-insurance/:id', {
    params: {
      id: id!,
    },
  });

  const fileUpload = fileUploadApis.usePost('/file/upload');

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [postBlanketInsurance, patchBlanketInsurance, fileUpload],
  });

  return (
    <form
      {...form.bindForm({
        async onSubmit(values) {
          if (type === 'create') {
            const { id } = await postBlanketInsurance.mutateAsync({
              ...values,
              blanket_insurance_files:
                values.local_blanket_insurance_files.length > 0
                  ? await fileUpload.mutateAsync({
                      file: values.local_blanket_insurance_files.map(({ file }) => file),
                    })
                  : [],
            });

            refresh();

            history.push({
              pathname: url('/utilities/BlanketInsurance/BlanketInsuranceDetails'),
              search: stringify({
                action: 'createSuccess',
                id,
              }),
            });
          } else {
            await patchBlanketInsurance.mutateAsync({
              ...values,
              blanket_insurance_files: await FileTypeHelper.uploadLocalArrayToRemote(
                values.local_blanket_insurance_files,
                fileUpload.mutateAsync,
              ),
            });

            refresh();

            history.push({
              pathname: url('/utilities/BlanketInsurance/BlanketInsuranceDetails'),
              search: stringify({
                action: 'editSuccess',
                id,
              }),
            });
          }
        },
      })}
    >
      {renderAlertsAndSpinnerModal()}
      <ManagementFormContainer mode="edit" containerProps={{ 'data-testid': 'BlanketInsurance' }}>
        <ManagementFormLabel field={fields.company} required />
        <ManagementFormInput field={fields.company} />

        <ManagementFormLabel field={fields.policy_no} required />
        <ManagementFormInput field={fields.policy_no} />

        <ManagementFormLabel field={fields.start} required />
        <div className={classes.dateRangeContainer}>
          <ManagementFormInput field={fields.start} />
          <span>{formStrings.from}</span>

          <ManagementFormInput field={fields.end} />
          <span>{formStrings.until}</span>
        </div>

        <ManagementFormLabel field={fields.local_blanket_insurance_files}></ManagementFormLabel>

        <div>
          <FileArrayInput
            field={fields.local_blanket_insurance_files}
            buttonProps={{
              color: 'outlined',
            }}
          />

          <div className={classes.hint}>{strings.localBlanketInsuranceFilesHint}</div>
        </div>

        <div className={classes.buttonContainer}>
          <Button
            text={type === 'create' ? strings.create : formStrings.update}
            color="primary"
            size="small"
            type="submit"
            style={{ width: 100 }}
          />

          {type === 'details' && (
            <Link
              to={{
                pathname: url('/utilities/BlanketInsurance/BlanketInsuranceDetails'),
                search: stringify({
                  id,
                }),
              }}
            >
              <Button text={formStrings.cancel} color="grey" size="small" style={{ width: 100 }} />
            </Link>
          )}
        </div>
      </ManagementFormContainer>
    </form>
  );
};

const BlanketInsuranceDisplay: React.FC<
  BlanketInsuranceCreateDetailsProps & {
    blanketInsurance: BlanketInsurance;
  }
> = ({ blanketInsurance }) => {
  const { id } = blanketInsurance;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const form = useForm(z.object({}));

  const { renderDialog, confirm } = useConfirmDialog();

  const deleteBlanketInsurance = blanketInsuranceApis.useDelete('/blanket-insurance/:id', {
    params: { id },
  });

  const { renderAlertsAndSpinnerModal } = useFormUtils({ mutations: [deleteBlanketInsurance] });

  const history = useHistory();

  return (
    <form
      {...form.bindForm({
        async onSubmit() {
          if ((await confirm({ message: strings.deleteHint })) === 'confirmed') {
            await deleteBlanketInsurance.mutateAsync(undefined);

            history.push(url('/utilities/BlanketInsurance/BlanketInsurances'));
          }
        },
      })}
    >
      {renderDialog()}
      {renderAlertsAndSpinnerModal()}

      <div className={classes.blanketInsuranceDisplay}>
        <div>{instructionStrings.insuranceCompany}</div>
        <div>{blanketInsurance.company || '-'}</div>

        <div>{instructionStrings.insurancePolicyNo}</div>
        <div>{blanketInsurance.policy_no || '-'}</div>

        <div>{instructionStrings.insurancePeriod}</div>
        <div>
          {formatDate(blanketInsurance.start)} - {formatDate(blanketInsurance.end)}
        </div>

        <div>{instructionStrings.additionalFiles}</div>
        <div className={classes.flexColumn}>
          {blanketInsurance.blanket_insurance_files.length === 0
            ? '-'
            : blanketInsurance.blanket_insurance_files.map((file) => (
                <a className={classes.link} href={file.url} download>
                  {file.name}
                </a>
              ))}
        </div>
      </div>

      <VerticalSpace height={20} />

      <div className={classes.buttonContainer}>
        <Link
          to={{
            pathname: url('/utilities/BlanketInsurance/BlanketInsuranceDetails'),
            search: stringify({
              id,
              action: 'edit',
            }),
          }}
        >
          <Button text={formStrings.edit} color="primary" size="small" style={{ width: 100 }} />
        </Link>

        <Button
          text={formStrings.delete}
          color="grey"
          size="small"
          style={{ width: 100 }}
          type="submit"
        />
      </div>
    </form>
  );
};

export interface BlanketInsuranceCreateDetailsProps {
  type: 'create' | 'details';
  id?: number;
  action?: BlanketInsuranceActionStatus;
}

export const BlanketInsuranceCreateDetails: React.FC<BlanketInsuranceCreateDetailsProps> = (
  props,
) => {
  const { type, id, action } = props;
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const blanketInsuranceQuery = blanketInsuranceApis.useGet(
    '/blanket-insurance/:id',
    {
      params: { id: id! },
    },
    {
      enabled: type === 'details',
    },
  );

  return (
    <Layout
      title={strings.title}
      breadcrumbs={[
        '/utilities/Dashboard',
        '/utilities/BlanketInsurance/BlanketInsurances',
        {
          text:
            type === 'details'
              ? blanketInsuranceQuery.data?.company ?? instructionStrings.insuranceTags.BLANKET
              : strings.createBlanketInsurance,
        },
      ]}
    >
      <div className={classes.body}>
        <Container>
          <VerticalSpace height={32} />
          <FormTitle title={strings.blanketInsuranceInfo} />

          {(action === 'createSuccess' || action === 'editSuccess') && (
            <Alert severity="success" open gutter>
              {strings[action]}
            </Alert>
          )}

          <QueryContainer queries={[blanketInsuranceQuery]}>
            {type === 'create' && <BlanketInsuranceCreateEditForm {...props} />}

            {type === 'details' &&
              (action === 'edit' ? (
                <BlanketInsuranceCreateEditForm
                  {...props}
                  blanketInsurance={blanketInsuranceQuery.data}
                />
              ) : (
                blanketInsuranceQuery.data && (
                  <BlanketInsuranceDisplay
                    {...props}
                    blanketInsurance={blanketInsuranceQuery.data}
                  />
                )
              ))}
          </QueryContainer>
        </Container>
      </div>
    </Layout>
  );
};
